<script>
  import { onMount } from 'svelte';
  import { format } from 'svelte-i18n';

  import { Porthole } from '@client/components/porthole';
  import { Select } from '@client/components/select';
  import { Spinner } from '@client/components/spinner';

  import { UsersService } from '@client/services/users';

  import { userStore } from '@client/stores/user';

  import { userMetadata } from '@client/utils/users';

  import { USER_REQUEST_PARAMS } from '@client/enums';

  export let value = null;
  export let placeholder = $format('label.PLACEHOLDER.SEARCH_USERS_DIAGNOSTICS');
  export let itemFilter = (label, filterText, option) =>
    `${JSON.stringify(option)}`.toLowerCase().indexOf(filterText.toLowerCase()) > -1;
  export let isClearable = true;
  export let showChevron = false;
  export let disabled = false;
  export let users = [];

  let originalUsers = [];
  let filterText = '';
  let previousSearchText = '';
  let searching = false;
  let selectedUser = null;

  const getUsers = async () => {
    let response = await UsersService.getUsers({
      ...USER_REQUEST_PARAMS,
      filterByDate: false,
      perPage: 200
    });

    users = originalUsers = processUsers(response?.data || []).sort((a, b) => a.label - b.label);
  };

  const processUsers = (data) => {
    return data.map((user) => {
      const username = user.email.split('@').shift();
      const meta = userMetadata({ ...user, features: $userStore.features });

      return {
        ...meta,
        username,
        label: meta.name,
        value: user.email
      };
    });
  };

  let userSearch = async () => await onUserSearch(filterText);

  let onUserSearch = async (text = '') => {
    const searchText = text;

    if (
      !searching &&
      searchText.length &&
      searchText !== previousSearchText &&
      JSON.stringify(users).toLowerCase().indexOf(searchText.toLowerCase()) === -1
    ) {
      searching = true;

      previousSearchText = searchText;

      const response = await UsersService.getUsers({
        ...USER_REQUEST_PARAMS,
        filterByDate: false,
        perPage: 200,
        debounceQuery: searchText.toLowerCase(),
        query: searchText.toLowerCase()
      });

      const results = processUsers(response.data);

      results.forEach((user) => {
        if (!users.find(({ value }) => value === user.value)) {
          users.push(user);
        }

        if (!originalUsers.find(({ value }) => value === user.value)) {
          originalUsers.push(user);
        }
      });
    } else {
      if (!searchText.length) {
        users = [...originalUsers];
      }
    }

    users = [...users].sort((a, b) => a.name.localeCompare(b.name));
    originalUsers = [...originalUsers].sort((a, b) => a.name.localeCompare(b.name));

    if (text) {
      selectedUser = users?.find(({ value }) => value.split('@').shift() === text) || { value: text, label: text };
    }

    searching = false;
  };

  onMount(getUsers);
</script>

<Select
  id="selecteduser"
  searchable
  clearable={isClearable}
  {showChevron}
  {placeholder}
  {itemFilter}
  {disabled}
  bind:filterText
  bind:items={users}
  bind:value
  on:filter={userSearch}
  on:change
  on:clear
>
  <svelte:fragment slot="item" let:item>
    <div class="item">
      {#key item}
        <Porthole details={item} />
      {/key}
      <div class="item-detail">
        <div class="name">{item.name}</div>
        <div class="email">{item.email}</div>
      </div>
    </div>
  </svelte:fragment>

  <svelte:fragment slot="empty">
    {#if searching}
      <div class="empty">
        <Spinner show />
      </div>
    {:else}
      <div class="empty">
        {$format('label.NO_OPTIONS_AVAILABLE')}
      </div>
    {/if}
  </svelte:fragment>
</Select>

<style lang="scss" src="./user-select.scss"></style>
